// ============================================================================
// perf-pessoa-mes — número da PESSOA no mês a partir do fk_performance
// ============================================================================
// 26/10/2026, pedido do dono: o Kayk saiu da Moema e foi para a Oscar Freire
// em outubro. O fk_performance guarda um registro por (pessoa, unidade, mês)
// (Moema R$ 9.328 + Oscar R$ 2.897), mas as telas pegavam um registro só (o
// primeiro .find) e o mês dele aparecia pela metade. "Some o resultado desse
// mês de Moema + Oscar para ele."
//
// Regra (a mesma decidida pelo dono em 16/10/2026 para a transferência):
//   - o número da PESSOA (Meu Desempenho, desafio, duelo, conquistas, ranking
//     da rede, faixa de comissão, Wrapped, push) SOMA as unidades do mês; a
//     meta do desafio não muda;
//   - o número da UNIDADE (ranking da unidade, Inteligência, Reunião Mensal)
//     mostra só a parte feita em cada unidade (perfNaUnidade).
// Sem segunda unidade no mês o resultado é o PRÓPRIO registro (mesmo objeto),
// então quem nunca trocou de unidade vê exatamente o que via antes.
//
// Puro (sem React, sem rede): o app usa por src/utils/perfPessoaMes.js e o
// servidor importa direto. Portado do E2 do ramo transferencia-unidade
// (api/_lib/lotacao.js, 16/10/2026, nunca publicado), sem a trilha de
// transferências: o rótulo do mês somado é a unidade atual do cadastro.
// ============================================================================

// Interruptor (regra "melhorar sem quebrar"): true = a soma das unidades e a
// parte de cada unidade, como descrito acima. false = o comportamento de antes
// em todas as telas (o primeiro registro da pessoa no mês, o mesclador com a
// chave sem unidade e a busca ao vivo só da unidade do cadastro); desliga com
// deploy, sem reverter código.
export const SOMA_UNIDADES_NO_MES = true;

export const ORDEM_UNIDADES = Object.freeze(['unit-vila', 'unit-moema', 'unit-itaim', 'unit-oscar', 'unit-jardins']);
const ordemUnidade = (u) => {
  const i = ORDEM_UNIDADES.indexOf(u);
  return i === -1 ? ORDEM_UNIDADES.length : i;
};

// Identidade do registro: (pessoa, unidade, ano, mês).
export function chaveRegistro(r) {
  if (!SOMA_UNIDADES_NO_MES) return `${r?.professionalId}|${r?.year}|${r?.month}`;
  return `${r?.professionalId}|${r?.unitId ?? ''}|${r?.year}|${r?.month}`;
}

// Somados entre unidades (só entram no resultado se algum registro tem o campo).
export const CAMPOS_SOMADOS = Object.freeze([
  'faturamento', 'comissao', 'servicosRealizados', 'clientesAtendidos',
  'agendamentosPreferenciais', 'agendamentosSemPreferencia', 'semPreferencia', 'geracaoDemanda',
  'produtosVendidos', 'faturamentoProdutos', 'valorServicosExtras', 'valorExtrasPacote',
  'valorPacotes', 'pacotesVendidosTotal', 'comandasPacote', 'faturamentoPacotes', 'clientesNovos',
  'bloqueiosAgenda', 'minutosBloqueadosAgenda',
]);
// Mapas {chave: quantidade} somados chave a chave.
export const MAPAS_SOMADOS = Object.freeze(['servicosExtras', 'pacotesVendidos']);

const num = (v) => Number(v) || 0;
const cents = (v) => Math.round(v * 100) / 100;
const tem = (regs, c) => regs.some(r => r[c] !== undefined && r[c] !== null);
const idDe = (quem) => (typeof quem === 'object' && quem ? quem.id : quem);

// Dentro de (pessoa, unidade) fica UM registro (duplicata: dois nomes do Avec
// no mesmo cadastro, ou registro zerado): maior faturamento, depois maior
// comissão, depois o primeiro. É a régua da folha (a maior dentro da unidade).
function escolherNaUnidade(regs) {
  let melhor = regs[0];
  for (const r of regs.slice(1)) {
    const df = num(r.faturamento) - num(melhor.faturamento);
    if (df > 0 || (df === 0 && num(r.comissao) > num(melhor.comissao))) melhor = r;
  }
  return melhor;
}

function somarMapas(vals) {
  const presentes = vals.filter(v => v !== undefined && v !== null);
  if (presentes.length === 0) return undefined;
  if (presentes.every(v => typeof v === 'number')) return presentes.reduce((s, v) => s + v, 0);
  const out = {};
  for (const v of presentes) {
    if (typeof v === 'number') { if (v) out._total = (out._total || 0) + v; continue; }
    if (!v || typeof v !== 'object') continue;
    for (const [k, q] of Object.entries(v)) out[k] = (out[k] || 0) + num(q);
  }
  return out;
}

// Todos os registros da pessoa no mês. Id comparado como texto e ano/mês como
// número (há leitor que recebia ano/mês em texto: presentation1on1Data).
export function registrosDaPessoaNoMes(perf, quem, ano, mes) {
  const profId = idDe(quem);
  if (profId == null || profId === '') return [];
  const pid = String(profId), y = Number(ano), m = Number(mes);
  const out = [];
  for (const r of (Array.isArray(perf) ? perf : [])) {
    if (r && String(r.professionalId) === pid && Number(r.year) === y && Number(r.month) === m) out.push(r);
  }
  return out;
}

// Um registro por unidade (ordem fixa das unidades), já sem duplicata.
export function perfPorUnidade(perf, quem, ano, mes) {
  const grupos = new Map();
  for (const r of registrosDaPessoaNoMes(perf, quem, ano, mes)) {
    const u = r.unitId ?? '';
    if (!grupos.has(u)) grupos.set(u, []);
    grupos.get(u).push(r);
  }
  return [...grupos.entries()]
    .sort((a, b) => ordemUnidade(a[0]) - ordemUnidade(b[0]))
    .map(([, regs]) => escolherNaUnidade(regs));
}

// Número da PESSOA no mês. `quem` = id ou o cadastro.
//   - nenhum registro → undefined (como o .find de antes);
//   - uma unidade só → o PRÓPRIO registro;
//   - duas ou mais → registro novo com CAMPOS_SOMADOS e MAPAS_SOMADOS somados,
//     ticket médio, ticket sem pacote e produtividade recalculados, `unitIds`
//     e `porUnidade` (o registro de cada unidade); o resto (id, metas,
//     observações) vem do registro da unidade atual do cadastro.
export function perfDoMes(perf, quem, ano, mes) {
  if (!SOMA_UNIDADES_NO_MES) return registrosDaPessoaNoMes(perf, quem, ano, mes)[0];
  const regs = perfPorUnidade(perf, quem, ano, mes);
  if (regs.length === 0) return undefined;
  if (regs.length === 1) return regs[0];
  const prof = typeof quem === 'object' && quem ? quem : null;
  const base = (prof && regs.find(r => r.unitId === prof.unitId)) || regs[0];
  const out = { ...base };
  for (const c of CAMPOS_SOMADOS) {
    if (tem(regs, c)) out[c] = cents(regs.reduce((s, r) => s + num(r[c]), 0));
  }
  for (const c of MAPAS_SOMADOS) {
    const v = somarMapas(regs.map(r => r[c]));
    if (v !== undefined) out[c] = v;
  }
  // Comissão com piso 0 POR UNIDADE antes de somar (Moema -50 e Oscar 800 = 800).
  if (tem(regs, 'comissao')) out.comissao = cents(regs.reduce((s, r) => s + Math.max(num(r.comissao), 0), 0));
  // Metas: o sync não grava meta; herda a do primeiro registro do mês que tiver.
  for (const c of ['metaMensal', 'metaTicketMedio', 'metaClientesPreferenciais']) {
    if (!(num(out[c]) > 0)) {
      const outro = regs.find(r => num(r[c]) > 0);
      if (outro) out[c] = outro[c];
    }
  }
  const fat = num(out.faturamento);
  const cli = num(out.clientesAtendidos);
  out.ticketMedio = cli > 0 ? cents(fat / cli) : 0;
  if (tem(regs, 'ticketMedioSemPacote')) {
    const pagas = Math.max(cli - num(out.comandasPacote), 0);
    out.ticketMedioSemPacote = pagas > 0 ? fat / pagas : 0;
  }
  if (tem(regs, 'tracaoVendas')) out.tracaoVendas = cli > 0 ? cents(num(out.servicosRealizados) / cli) : 0;
  out.unitIds = regs.map(r => r.unitId);
  out.porUnidade = regs;
  return out;
}

// Meses da pessoa (um por mês, somado), em ordem crescente. opts.ano filtra.
export function perfMesesDaPessoa(perf, quem, { ano } = {}) {
  const profId = idDe(quem);
  if (profId == null || profId === '') return [];
  const pid = String(profId);
  const meses = new Map();
  for (const r of (Array.isArray(perf) ? perf : [])) {
    if (!r || String(r.professionalId) !== pid) continue;
    const y = Number(r.year), m = Number(r.month);
    if (ano !== undefined && y !== Number(ano)) continue;
    const k = `${y}-${m}`;
    if (!meses.has(k)) meses.set(k, { year: y, month: m });
  }
  return [...meses.values()]
    .sort((a, b) => (a.year - b.year) || (a.month - b.month))
    .map(({ year, month }) => perfDoMes(perf, quem, year, month));
}

// Parte da pessoa feita NA unidade no mês (ranking da unidade, Inteligência).
// Registro sem unitId (antigo) conta como da unidade atual do cadastro.
export function perfNaUnidade(perf, quem, unitId, ano, mes) {
  if (!SOMA_UNIDADES_NO_MES) return registrosDaPessoaNoMes(perf, quem, ano, mes)[0];
  const prof = typeof quem === 'object' && quem ? quem : null;
  const regs = registrosDaPessoaNoMes(perf, quem, ano, mes)
    .filter(r => (r.unitId ?? (prof ? prof.unitId : undefined)) === unitId);
  return regs.length ? escolherNaUnidade(regs) : undefined;
}

// Unidades em que a pessoa tem número nos meses dados ([{y, m}] ou [{year, month}]),
// com a unidade atual do cadastro sempre primeiro. É a lista de unidades que a
// busca ao vivo do período precisa trazer para somar a pessoa.
export function unidadesDaPessoaNosMeses(perf, prof, meses = []) {
  if (!prof) return [];
  if (!SOMA_UNIDADES_NO_MES) return [prof.unitId].filter(Boolean);
  const alvo = new Set((meses || []).map(x => `${Number(x.y ?? x.year)}-${Number(x.m ?? x.month)}`));
  const achadas = new Set();
  for (const r of (Array.isArray(perf) ? perf : [])) {
    if (!r || String(r.professionalId) !== String(prof.id) || !r.unitId) continue;
    if (alvo.has(`${Number(r.year)}-${Number(r.month)}`)) achadas.add(r.unitId);
  }
  achadas.delete(prof.unitId);
  return [prof.unitId, ...[...achadas].sort((a, b) => ordemUnidade(a) - ordemUnidade(b))].filter(Boolean);
}

// Meses (y, m) que um período ISO 'YYYY-MM-DD'..'YYYY-MM-DD' toca.
export function mesesDoPeriodo(iniISO, fimISO) {
  const p = (s) => {
    const m = /^(\d{4})-(\d{2})/.exec(String(s || ''));
    return m ? { y: Number(m[1]), m: Number(m[2]) } : null;
  };
  const a = p(iniISO), b = p(fimISO);
  if (!a || !b) return [];
  const out = [];
  let { y, m } = a;
  for (let i = 0; i < 36 && (y < b.y || (y === b.y && m <= b.m)); i++) {
    out.push({ y, m });
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return out;
}
